import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, GripVertical, Upload, X } from 'lucide-react';
import { uploadImage } from '../../services/api';

interface Props {
  images: string[];
  onChange: (next: string[]) => void;
  productId?: string;
  // Optional: when provided, each image gets a "color tag" dropdown so the
  // customer carousel can filter to matching images when a color is picked.
  colorOptions?: string[];
  colorMap?: Record<string, string>;
  onColorMapChange?: (next: Record<string, string>) => void;
}

const ProductImagesEditor: React.FC<Props> = ({
  images,
  onChange,
  productId,
  colorOptions,
  colorMap,
  onColorMapChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const dragFrom = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(undefined);
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        const meta: Record<string, string> = {};
        if (productId) meta.product_id = productId;
        const { s3_url } = await uploadImage(file, 'product', meta);
        uploaded.push(s3_url);
      }
      onChange([...images, ...uploaded]);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (idx: number) => {
    const removedUrl = images[idx];
    onChange(images.filter((_, i) => i !== idx));
    if (removedUrl && colorMap && onColorMapChange && colorMap[removedUrl]) {
      const next = { ...colorMap };
      delete next[removedUrl];
      onColorMapChange(next);
    }
  };

  const setColorFor = (url: string, color: string) => {
    if (!onColorMapChange) return;
    const next = { ...(colorMap ?? {}) };
    if (color) next[url] = color;
    else delete next[url];
    onColorMapChange(next);
  };

  /**
   * Move an image within the list.
   *
   * Order is what the customer sees: the first image is the one on the
   * shop grid and the one a product page opens on, so which photo leads
   * is a merchandising decision rather than an upload accident.
   */
  const moveTo = (from: number, to: number) => {
    if (from === to || to < 0 || to >= images.length) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const showColorPicker = !!colorOptions && colorOptions.length > 0 && !!onColorMapChange;

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Images</div>
      {showColorPicker && (
        <p className="text-xs text-text-light">
          Tag an image with a color to make it appear only when the customer picks that color.
          Untagged images show by default.
        </p>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((url, idx) => (
            <div
              key={url + idx}
              draggable
              onDragStart={() => {
                dragFrom.current = idx;
              }}
              onDragEnd={() => {
                dragFrom.current = null;
                setDragOver(undefined);
              }}
              // Without preventDefault the browser refuses the drop.
              onDragOver={e => {
                e.preventDefault();
                setDragOver(idx);
              }}
              onDragLeave={() => setDragOver(prev => (prev === idx ? undefined : prev))}
              onDrop={e => {
                e.preventDefault();
                setDragOver(undefined);
                if (dragFrom.current !== null) moveTo(dragFrom.current, idx);
                dragFrom.current = null;
              }}
              className={`space-y-1 rounded ${
                dragOver === idx ? 'ring-2 ring-primary ring-offset-2' : ''
              }`}
            >
              <div className="relative group cursor-grab active:cursor-grabbing">
                <img
                  src={url}
                  alt={`Product ${idx + 1}`}
                  draggable={false}
                  className="w-full h-32 object-cover rounded border border-background-dark"
                />
                {/* Position is worth showing: "first" is the one that ends
                    up on the shop grid, which isn't obvious from a grid. */}
                <span className="absolute top-1 left-1 inline-flex items-center gap-1 bg-white/90 text-text-light rounded-full pl-1.5 pr-2 py-0.5 text-[10px] shadow">
                  <GripVertical size={10} />
                  {idx === 0 ? 'Cover' : idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  title="Remove"
                >
                  <X size={14} />
                </button>
                {/* Dragging is unavailable on a touch screen and to a
                    keyboard, so the same move is always a button too. */}
                <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                  <button
                    type="button"
                    onClick={() => moveTo(idx, idx - 1)}
                    disabled={idx === 0}
                    aria-label={`Move image ${idx + 1} earlier`}
                    className="bg-white/90 rounded-full p-1 shadow disabled:opacity-30"
                  >
                    <ChevronLeft size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveTo(idx, idx + 1)}
                    disabled={idx === images.length - 1}
                    aria-label={`Move image ${idx + 1} later`}
                    className="bg-white/90 rounded-full p-1 shadow disabled:opacity-30"
                  >
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>
              {showColorPicker && (
                <select
                  value={colorMap?.[url] ?? ''}
                  onChange={e => setColorFor(url, e.target.value)}
                  className="w-full border border-background-dark rounded px-2 py-1 text-xs bg-white"
                >
                  <option value="">All colors</option>
                  {colorOptions!.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={e => handleFiles(e.target.files)}
          disabled={uploading}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 border border-background-dark rounded px-3 py-2 text-sm hover:border-primary disabled:opacity-50"
        >
          <Upload size={14} />
          {uploading ? 'Uploading…' : images.length ? 'Add more images' : 'Upload images'}
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default ProductImagesEditor;
